// Camera and microphone pre-flight, shown before the first question.
//
// THE FAILURE THIS EXISTS TO PREVENT
// A candidate whose microphone is muted, or routed to a headset that is not
// connected, can answer all twelve questions perfectly. Every recording is
// silent, every transcript is empty, and the only remedy is asking them to sit
// the whole interview again — by which point they have already given up half an
// hour and their goodwill.
//
// Checking that the camera renders is easy and nearly always passes. Checking
// the microphone is the part that matters, and it cannot be done by asking the
// browser whether a track exists: a muted mic reports a perfectly healthy track
// that produces silence. The only reliable test is to measure actual signal,
// which is what the analyser below does — the candidate speaks, and we watch for
// the level to move.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Stack,
  LinearProgress,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MicIcon from "@mui/icons-material/Mic";
// The same capture the interview uses for the audio a transcript is made from,
// so a practice answer exercises the real path rather than a simulation of it.
import { startWavCapture } from "./wavRecorder";

// Peak level, 0-100, that counts as "this microphone is genuinely working".
// Set low enough for a quiet speaker on a laptop mic, high enough that room
// noise alone will not clear it.
const MIC_PASS_THRESHOLD = 12;

const DeviceCheck = ({ stream, onPass, onRetry }) => {
  const videoRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const peakRef = useRef(0);

  const [level, setLevel] = useState(0);
  const [peak, setPeak] = useState(0);
  const [cameraOk, setCameraOk] = useState(false);

  // The practice answer. Nothing here is uploaded or kept — it exists so the
  // candidate performs the start/speak/stop loop once before it costs them a
  // real question. See the panel in the render for why.
  const [practiceState, setPracticeState] = useState("idle"); // idle|recording
  const [practiceResult, setPracticeResult] = useState(null);
  const practiceRef = useRef(null);

  const startPractice = useCallback(async () => {
    if (!stream) return;
    setPracticeResult(null);
    setPracticeState("recording");
    try {
      practiceRef.current = await startWavCapture(stream);
      if (!practiceRef.current) throw new Error("capture unavailable");
    } catch (err) {
      console.warn("[deviceCheck] practice capture failed:", err?.message);
      // Unavailable capture must not look like a failed candidate. Say nothing
      // rather than tell them their microphone is broken when ours is.
      setPracticeState("idle");
      practiceRef.current = null;
    }
  }, [stream]);

  const stopPractice = useCallback(async () => {
    setPracticeState("idle");
    const capture = practiceRef.current;
    practiceRef.current = null;
    if (!capture) return;
    try {
      const result = await capture.stop();
      setPracticeResult({
        hasSpeech: Boolean(result?.hasSpeech),
        seconds: Math.max(1, Math.round(result?.durationSeconds || 0)),
      });
    } catch (err) {
      console.warn("[deviceCheck] practice stop failed:", err?.message);
    }
  }, []);

  // A capture still running when this screen unmounts holds an AudioContext and
  // a live worklet node against a track the interview is about to take over.
  useEffect(
    () => () => {
      practiceRef.current?.cancel?.();
      practiceRef.current = null;
    },
    [],
  );

  const micOk = peak >= MIC_PASS_THRESHOLD;

  useEffect(() => {
    if (!stream) return undefined;

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      // A video track that exists but never produces a frame is a real failure
      // mode on some Android browsers, so treat "playing" as the signal rather
      // than "track present".
      videoRef.current.onplaying = () => setCameraOk(true);
    }

    let cancelled = false;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (cancelled) return;
        analyser.getByteTimeDomainData(data);
        // Deviation from the 128 midpoint is the amplitude. Peak rather than
        // average, because a candidate saying one short word must be enough.
        let max = 0;
        for (let i = 0; i < data.length; i++) {
          max = Math.max(max, Math.abs(data[i] - 128));
        }
        const scaled = Math.min(100, Math.round((max / 128) * 100 * 2));
        setLevel(scaled);
        if (scaled > peakRef.current) {
          peakRef.current = scaled;
          setPeak(scaled);
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // No analyser available. Rather than blocking, fall through with the mic
      // unverified — an imperfect check must not stop someone interviewing.
      setPeak(MIC_PASS_THRESHOLD);
    }

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current?.state !== "closed") {
        audioCtxRef.current?.close().catch(() => {});
      }
    };
  }, [stream]);

  const handleContinue = useCallback(() => {
    onPass?.({
      camera_ok: cameraOk,
      mic_ok: micOk,
      mic_peak_level: peak,
      browser: navigator.userAgent,
      screen: `${window.screen?.width}x${window.screen?.height}`,
      connection: navigator.connection?.effectiveType || "unknown",
    });
  }, [cameraOk, micOk, peak, onPass]);

  const Row = ({ ok, label, detail }) => (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {ok ? (
        <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 22 }} />
      ) : (
        <ErrorOutlineIcon sx={{ color: "#d97706", fontSize: 22 }} />
      )}
      <Box>
        <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{label}</Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
          {detail}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
        Quick check before we start
      </Typography>
      <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
        This takes about ten seconds and saves you from recording an interview
        nobody can hear.
      </Typography>

      <Box
        sx={{
          mt: 2.5,
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#0f172a",
          aspectRatio: "4 / 3",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      <Stack spacing={2} sx={{ mt: 2.5 }}>
        <Row
          ok={cameraOk}
          label="Camera"
          detail={cameraOk ? "Working — you should see yourself above." : "Waiting for the camera…"}
        />

        <Box>
          <Row
            ok={micOk}
            label="Microphone"
            detail={
              micOk
                ? "Working — we can hear you."
                : "Say something out loud, like your name."
            }
          />
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1, pl: 4.5 }}>
            <MicIcon sx={{ fontSize: 18, color: micOk ? "#16a34a" : "#94a3b8" }} />
            <LinearProgress
              variant="determinate"
              value={level}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: micOk ? "#16a34a" : "#f59e0b",
                  transition: "none",
                },
              }}
            />
            {micOk && (
              <Chip
                size="small"
                label="Detected"
                sx={{ bgcolor: "#dcfce7", color: "#166534", fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      {!micOk && (
        <Alert severity="warning" sx={{ mt: 2.5, borderRadius: 2 }}>
          We cannot hear you yet. Check that the right microphone is selected and
          that it is not muted, then speak again.
        </Alert>
      )}

      {/* ── One practice answer ─────────────────────────────────────────────
          The level meter above proves the microphone produces signal. It does
          NOT prove the candidate knows what the interview will ask of them,
          and that is what was actually going wrong: people reached question
          one without having pressed a record button in their life, forgot to
          press it, and answered to a camera that was not recording.

          So: one rehearsal of the exact loop — start, speak, stop, told what
          we heard — on a question that does not count and is never uploaded.

          It exercises startWavCapture, which is the same path that produces
          the audio the transcript is made from. A microphone that passes the
          meter but fails here is a real failure this catches before it costs
          somebody an answer rather than after.

          Never a gate. It can be skipped, for the same reason the mic check
          can: a false negative must not stand between anyone and a job. */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 2,
          border: "1px solid #e7e5e4",
          bgcolor: practiceResult?.hasSpeech ? "#f0fdf4" : "#fafaf9",
        }}
      >
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
          Try it once before we start
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.5, lineHeight: 1.6 }}>
          {practiceState === "recording"
            ? "Say your name out loud, then press done — exactly as you will in the interview."
            : "Every question works the same way: it's read to you, recording starts, you answer, you press done. This one doesn't count and isn't saved."}
        </Typography>

        {practiceState === "recording" ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              onClick={stopPractice}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: 2,
                bgcolor: "#f43f5e", "&:hover": { bgcolor: "#e11d48" },
              }}
            >
              Done
            </Button>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 9, height: 9, borderRadius: "50%", bgcolor: "#f43f5e",
                  animation: "hBreathe 1.2s ease-in-out infinite",
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f43f5e" }}>
                Recording
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            onClick={startPractice}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            {practiceResult ? "Try again" : "Start practice answer"}
          </Button>
        )}

        {practiceResult && (
          <Alert
            severity={practiceResult.hasSpeech ? "success" : "warning"}
            sx={{ mt: 1.5, borderRadius: 2, fontSize: 13 }}
          >
            {practiceResult.hasSpeech
              ? `That's exactly it — we heard you clearly across ${practiceResult.seconds} second${
                  practiceResult.seconds === 1 ? "" : "s"
                }. The real questions work the same way.`
              : "We recorded that but couldn't hear any speech. Check your microphone isn't muted — including a switch on a headset — and try once more."}
          </Alert>
        )}
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="large"
        disabled={!cameraOk || practiceState === "recording"}
        onClick={handleContinue}
        sx={{
          mt: 3,
          py: 1.4,
          borderRadius: 2,
          textTransform: "none",
          fontSize: 16,
          fontWeight: 700,
          bgcolor: "#0b8a8f",
          "&:hover": { bgcolor: "#097077" },
        }}
      >
        {micOk ? "Everything looks good — start" : "Start anyway"}
      </Button>

      {!micOk && cameraOk && (
        // Never a hard block. A false negative here — an unusual device, a
        // browser that will not expose an analyser — would lock someone out of
        // a job application entirely, which is far worse than a flagged
        // recording a reviewer can interpret.
        <Typography
          sx={{ mt: 1.5, fontSize: 12.5, color: "text.secondary", textAlign: "center" }}
        >
          You can continue without the microphone check, but your answers may not
          be audible.
        </Typography>
      )}

      <Button
        fullWidth
        variant="text"
        onClick={onRetry}
        sx={{ mt: 1, textTransform: "none", color: "text.secondary" }}
      >
        Reload devices
      </Button>
    </Box>
  );
};

export default DeviceCheck;
